
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface StatData {
  label: string;
  value: number;
}

interface AdminStats {
  total_revenue: number;
  total_unpaid_revenue: number;
  total_paid_bills: number;
  total_pending_bills: number;
  total_cancelled_bills: number;
  product_subscriptions: StatData[];
  plan_subscriptions: StatData[];
  school_subscriptions: StatData[];
}

export const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const parseSubscriptionData = (data: unknown): StatData[] => {
    if (Array.isArray(data)) {
      return data;
    }
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return [];
      }
    }
    return [];
  };

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First check if the user is authenticated
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("Session error:", sessionError);
        setLoading(false);
        return;
      }
      
      if (!sessionData.session) {
        // Handle not authenticated silently
        setLoading(false);
        return;
      }
      
      // Get the statistical data from the database function
      console.log("Fetching admin statistics...");
      const { data, error } = await supabase.rpc('get_subscription_statistics');
      
      if (error) {
        console.error("Error fetching stats:", error);
        setError(error);
        return;
      }

      if (data && data[0]) {
        console.log("Raw admin stats data:", data[0]);
        
        // Make sure we're using Number to convert from string values
        const transformedStats: AdminStats = {
          total_revenue: Number(data[0].total_revenue) || 0,
          total_unpaid_revenue: Number(data[0].total_unpaid_revenue) || 0,
          total_paid_bills: Number(data[0].total_paid_bills) || 0,
          total_pending_bills: Number(data[0].total_pending_bills) || 0,
          total_cancelled_bills: Number(data[0].total_cancelled_bills) || 0,
          product_subscriptions: parseSubscriptionData(data[0].product_subscriptions) || [],
          plan_subscriptions: parseSubscriptionData(data[0].plan_subscriptions) || [],
          school_subscriptions: parseSubscriptionData(data[0].school_subscriptions) || [],
        };

        console.log("Transformed admin stats:", transformedStats);
        
        // Also fetch the actual bills for verification 
        const { data: billsData, error: billsError } = await supabase
          .from('bills')
          .select('id, amount, status')
          .eq('status', 'paid');
          
        if (billsError) {
          console.error("Error fetching bills:", billsError);
        } else if (billsData) {
          // Calculate the sum directly from bills
          const paidSum = billsData.reduce((sum, bill) => sum + Number(bill.amount), 0);
          console.log("Revenue verification from bills table:", {
            paidBillsCount: billsData.length,
            calculatedPaidRevenue: paidSum,
            reportedPaidRevenue: transformedStats.total_revenue,
            match: Math.abs(paidSum - transformedStats.total_revenue) < 1, // Check if values match accounting for rounding
            bills: billsData.map(b => ({ id: b.id, amount: b.amount }))
          });
        }
        
        setStats(transformedStats);
      } else {
        // Provide default empty stats if no data returned
        setStats({
          total_revenue: 0,
          total_unpaid_revenue: 0,
          total_paid_bills: 0,
          total_pending_bills: 0,
          total_cancelled_bills: 0,
          product_subscriptions: [],
          plan_subscriptions: [],
          school_subscriptions: [],
        });
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setError(error instanceof Error ? error : new Error('Unknown error'));
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch statistics",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Run fetchStats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, fetchStats };
};
